import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Send, Slack, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Unified comments thread (Studio + Slack), rendered inline in ApprovalPage ContentCard.
 */
export default function CommentsThread({ contentId, currentUserId }) {
  const [comments, setComments] = useState([]);
  const [newText, setNewText] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/content/${contentId}/comments`, { withCredentials: true });
      setComments(res.data);
    } catch {
      // ignore
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contentId]);

  const send = async () => {
    const text = newText.trim();
    if (!text) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/content/${contentId}/comments`,
        { text },
        { withCredentials: true }
      );
      setNewText("");
      await load();
    } catch {
      toast.error("Kommentar konnte nicht gesendet werden");
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/content/${contentId}/comments/${id}`, { withCredentials: true });
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch {
      toast.error("Loeschen fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-3" data-testid={`comments-thread-${contentId}`}>
      <div className="flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-[var(--studio-text-muted)]" />
        <p className="text-xs font-medium text-[var(--studio-text-muted)]">
          Kommentare ({comments.length})
        </p>
      </div>

      {comments.length > 0 && (
        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {comments.map((c) => {
            const isSlack = c.source === "slack";
            const canDelete = c.author_id === currentUserId;
            return (
              <div
                key={c.id}
                data-testid={`comment-${c.id}`}
                className={`p-3 rounded-lg text-sm border ${
                  isSlack ? "bg-[#4A154B]/5 border-[#4A154B]/20" : "bg-[var(--studio-bg)] border-[var(--studio-border)]"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {isSlack && <Slack className="w-3 h-3 text-[#4A154B]" />}
                    <span className="text-xs font-semibold">{c.author}</span>
                    <span className="text-[10px] text-[var(--studio-text-muted)]">
                      {new Date(c.created_at).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  {canDelete && (
                    <button
                      onClick={() => remove(c.id)}
                      data-testid={`delete-comment-${c.id}`}
                      className="text-[var(--studio-text-muted)] hover:text-red-500"
                      aria-label="Kommentar loeschen"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <p className="whitespace-pre-wrap">{c.text}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex gap-2">
        <Textarea
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Kommentar schreiben..."
          rows={2}
          data-testid={`comment-input-${contentId}`}
          className="text-sm flex-1"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Button
          size="sm"
          onClick={send}
          disabled={sending || !newText.trim()}
          data-testid={`comment-send-${contentId}`}
          className="bg-[#2563EB] hover:bg-[#1D4ED8] self-end"
        >
          <Send className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}
